import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllNotes } from "./NoteService";
import { addLink, getBacklinks, getLinks, removeLink } from "./NoteGraphService";
import "./NoteGraphPanel.css";

export default function NoteGraphPanel({ noteId }) {
  const nav = useNavigate();
  const [links, setLinks] = useState([]);      // outgoing links (this note → others)
  const [backlinks, setBacklinks] = useState([]); // incoming links (others → this note)
  const [allNotes, setAllNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [notesIndex, setNotesIndex] = useState({});

  useEffect(() => {
    if (!noteId) return;
    Promise.all([getLinks(noteId), getBacklinks(noteId), getAllNotes()]).then(([lk, bl, notes]) => {
      setLinks(lk);
      setBacklinks(bl);
      const idx = {};
      (notes || []).forEach(n => { idx[n.id] = n; });
      setNotesIndex(idx);
      setAllNotes(notes || []);
    });
  }, [noteId]);

  const handleAdd = async (targetId) => {
    await addLink(noteId, targetId);
    const updated = await getLinks(noteId);
    setLinks(updated);
    setSearch("");
  };

  const handleRemove = async (targetId) => {
    await removeLink(noteId, targetId);
    setLinks(prev => prev.filter(l => l.entityId !== targetId));
  };

  const linkedIds = new Set(links.map(l => l.entityId));
  const filtered = search.trim()
    ? allNotes.filter(n => n.id !== noteId && !linkedIds.has(n.id) &&
        n.title?.toLowerCase().includes(search.toLowerCase()))
    : [];

  return (
    <div className="ngp-panel">
      <h4 className="ngp-title">Linked Notes</h4>

      {/* GRAPH SVG — simple circular layout */}
      {(links.length > 0 || backlinks.length > 0) && (
        <GraphSvg noteId={noteId} notesIndex={notesIndex} links={links} backlinks={backlinks}
                   onNavigate={(id) => nav("/notes", { state: { noteId: id } })} />
      )}

      {/* Outgoing links */}
      <div className="ngp-section">
        <div className="ngp-section-label">Links from this note ({links.length})</div>
        {links.map(l => {
          const n = notesIndex[l.entityId];
          return (
            <div key={l.id} className="ngp-link-row">
              <button className="ngp-link-title" onClick={() => nav("/notes", { state: { noteId: l.entityId } })}>
                {n?.title || l.entityId}
              </button>
              <button className="ngp-remove" onClick={() => handleRemove(l.entityId)}>×</button>
            </div>
          );
        })}
      </div>

      {/* Search to add link */}
      <div className="ngp-add-row">
        <input
          className="ngp-search"
          placeholder="Link another note…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {filtered.length > 0 && (
          <div className="ngp-suggestions">
            {filtered.slice(0, 6).map(n => (
              <button key={n.id} className="ngp-suggestion" onClick={() => handleAdd(n.id)}>
                {n.title}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Backlinks */}
      {backlinks.length > 0 && (
        <div className="ngp-section">
          <div className="ngp-section-label">Referenced by ({backlinks.length})</div>
          {backlinks.map(bl => {
            const n = notesIndex[bl.noteId];
            return (
              <div key={bl.id} className="ngp-link-row">
                <button className="ngp-link-title" onClick={() => nav("/notes", { state: { noteId: bl.noteId } })}>
                  ← {n?.title || bl.noteId}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GraphSvg({ noteId, notesIndex, links, backlinks, onNavigate }) {
  const W = 260, H = 180, CX = 130, CY = 90, R = 65;
  const nodes = [
    { id: noteId, label: notesIndex[noteId]?.title || "This note", cx: CX, cy: CY, current: true },
  ];
  const allLinked = [...new Set([...links.map(l => l.entityId), ...backlinks.map(b => b.noteId)])];
  allLinked.slice(0, 8).forEach((id, i) => {
    const angle = (2 * Math.PI * i) / Math.min(allLinked.length, 8) - Math.PI / 2;
    nodes.push({ id, label: notesIndex[id]?.title || id, cx: CX + R * Math.cos(angle), cy: CY + R * Math.sin(angle) });
  });

  return (
    <svg className="ngp-svg" viewBox={`0 0 ${W} ${H}`}>
      {nodes.slice(1).map(n => (
        <line key={n.id} x1={CX} y1={CY} x2={n.cx} y2={n.cy} stroke="var(--color-border-soft)" strokeWidth="1.5" />
      ))}
      {nodes.map(n => (
        <g key={n.id} onClick={() => !n.current && onNavigate(n.id)} style={{ cursor: n.current ? "default" : "pointer" }}>
          <circle cx={n.cx} cy={n.cy} r={n.current ? 14 : 10}
            fill={n.current ? "var(--color-primary,#2563eb)" : "var(--color-surface-subtle)"}
            stroke={n.current ? "#2563eb" : "var(--color-border-soft)"} strokeWidth="1.5" />
          <text x={n.cx} y={n.cy + (n.current ? 24 : 20)} textAnchor="middle"
            fontSize={n.current ? "9" : "8"} fill="var(--color-muted)"
            style={{ pointerEvents: "none" }}>
            {(n.label || "").slice(0, 14)}
          </text>
        </g>
      ))}
    </svg>
  );
}
